import { createTheme, type Theme, type ThemeOptions } from '@mui/material';
import {
  focusRing,
  getStatusColours,
  type StatusColours,
} from './tokens';

// Expose semantic status colours on the MUI theme so screens can use
// `theme.palette.status.atRisk` without importing tokens directly.
declare module '@mui/material/styles' {
  interface Palette {
    status: StatusColours;
  }
  interface PaletteOptions {
    status?: StatusColours;
  }
}

// ---------------------------------------------------------------------------
// Theme Preferences
// ---------------------------------------------------------------------------

export interface ThemePreferences {
  mode: 'dark' | 'light' | 'system';
  highContrast: boolean;
  fontFamily: 'default' | 'dyslexia' | 'dyslexia-hc';
  fontSize: 'small' | 'medium' | 'large' | 'xl';
  reducedMotion: boolean;
}

export const defaultPreferences: ThemePreferences = {
  mode: 'dark',
  highContrast: false,
  fontFamily: 'default',
  fontSize: 'medium',
  reducedMotion: false,
};

// ---------------------------------------------------------------------------
// Palette definitions
// ---------------------------------------------------------------------------

const darkPalette: ThemeOptions['palette'] = {
  mode: 'dark',
  primary: {
    main: '#6C9BFF',
    light: '#9DBFFF',
    dark: '#3D6FCC',
  },
  secondary: {
    main: '#FF8A65',
    light: '#FFBB93',
    dark: '#C75B39',
  },
  background: {
    default: '#0A0E17',
    paper: '#121829',
  },
  error: { main: '#FF5252' },
  warning: { main: '#FFB74D' },
  success: { main: '#69F0AE' },
  text: {
    primary: '#E8ECF4',
    secondary: '#8892A8',
  },
  divider: 'rgba(255,255,255,0.08)',
};

const lightPalette: ThemeOptions['palette'] = {
  mode: 'light',
  primary: {
    main: '#3D6FCC',
    light: '#6C9BFF',
    dark: '#2A4E8F',
  },
  secondary: {
    main: '#C75B39',
    light: '#FF8A65',
    dark: '#9A3F24',
  },
  background: {
    default: '#FAFBFD',
    paper: '#FFFFFF',
  },
  error: { main: '#D32F2F' },
  warning: { main: '#ED6C02' },
  success: { main: '#2E7D32' },
  text: {
    primary: '#1A1F2E',
    secondary: '#5A6478',
  },
  divider: 'rgba(0,0,0,0.08)',
};

const highContrastDarkPalette: ThemeOptions['palette'] = {
  mode: 'dark',
  primary: {
    main: '#80B0FF',
    light: '#B3D1FF',
    dark: '#4D7FE6',
  },
  secondary: {
    main: '#FFA080',
    light: '#FFC4B0',
    dark: '#E06040',
  },
  background: {
    default: '#000000',
    paper: '#0D0D0D',
  },
  error: { main: '#FF6B6B' },
  warning: { main: '#FFC857' },
  success: { main: '#7DFFBE' },
  text: {
    primary: '#FFFFFF',
    secondary: '#B0B8CC',
  },
  divider: 'rgba(255,255,255,0.2)',
};

const highContrastLightPalette: ThemeOptions['palette'] = {
  mode: 'light',
  primary: {
    main: '#1A3D7A',
    light: '#3D6FCC',
    dark: '#0D2650',
  },
  secondary: {
    main: '#8B3A1F',
    light: '#C75B39',
    dark: '#5C2614',
  },
  background: {
    default: '#FFFFFF',
    paper: '#FFFFFF',
  },
  error: { main: '#B71C1C' },
  warning: { main: '#BF360C' },
  success: { main: '#1B5E20' },
  text: {
    primary: '#000000',
    secondary: '#1A1A1A',
  },
  divider: 'rgba(0,0,0,0.2)',
};

// ---------------------------------------------------------------------------
// Dyslexia-friendly palettes
// ---------------------------------------------------------------------------

const dyslexiaPalette: ThemeOptions['palette'] = {
  mode: 'light',
  primary: {
    main: '#1D6B50',
    light: '#EDF5F1',
    dark: '#145740',
  },
  secondary: {
    main: '#8B6914',
    light: '#FBF6E9',
    dark: '#6B5010',
  },
  background: {
    default: '#FAF8F5',
    paper: '#F5F2ED',
  },
  error: { main: '#9E3B2D' },
  warning: { main: '#8B6914' },
  success: { main: '#1D6B50' },
  text: {
    primary: '#353535',
    secondary: '#636058',
  },
  divider: '#E0DCD5',
};

const dyslexiaHighContrastPalette: ThemeOptions['palette'] = {
  mode: 'light',
  primary: {
    main: '#0F5A3E',
    light: '#E4F2EC',
    dark: '#0A4830',
  },
  secondary: {
    main: '#7A5B0F',
    light: '#FAF3E2',
    dark: '#5C4409',
  },
  background: {
    default: '#FFFEF9',
    paper: '#FFF9EE',
  },
  error: { main: '#8B2E22' },
  warning: { main: '#7A5B0F' },
  success: { main: '#0F5A3E' },
  text: {
    primary: '#1C1B18',
    secondary: '#4A4740',
  },
  divider: '#C4BFB4',
};

// ---------------------------------------------------------------------------
// Font size map (base htmlFontSize)
// ---------------------------------------------------------------------------

// `medium` is 15 to meet the §7.3 minimum body-size requirement.
const fontSizeMap: Record<ThemePreferences['fontSize'], number> = {
  small: 13,
  medium: 15,
  large: 17,
  xl: 19,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function isDyslexiaTheme(fontFamily: ThemePreferences['fontFamily']): boolean {
  return fontFamily === 'dyslexia' || fontFamily === 'dyslexia-hc';
}

function getPalette(
  resolvedMode: 'dark' | 'light',
  highContrast: boolean,
  fontFamily: ThemePreferences['fontFamily'],
): ThemeOptions['palette'] {
  if (fontFamily === 'dyslexia-hc') return dyslexiaHighContrastPalette;
  if (fontFamily === 'dyslexia') return dyslexiaPalette;
  if (highContrast) {
    return resolvedMode === 'dark' ? highContrastDarkPalette : highContrastLightPalette;
  }
  return resolvedMode === 'dark' ? darkPalette : lightPalette;
}

const DEFAULT_FONT = '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const DYSLEXIA_FONT = '"Atkinson Hyperlegible", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

function getFontFamily(font: ThemePreferences['fontFamily']): string {
  return isDyslexiaTheme(font) ? DYSLEXIA_FONT : DEFAULT_FONT;
}

function getCardBorder(resolvedMode: 'dark' | 'light', highContrast: boolean, fontFamily: ThemePreferences['fontFamily']): string {
  if (fontFamily === 'dyslexia-hc') return '1px solid #C4BFB4';
  if (fontFamily === 'dyslexia') return '1px solid #E0DCD5';
  if (highContrast) {
    return resolvedMode === 'dark'
      ? '1px solid rgba(255,255,255,0.2)'
      : '1px solid rgba(0,0,0,0.2)';
  }
  return resolvedMode === 'dark'
    ? '1px solid rgba(255,255,255,0.06)'
    : '1px solid rgba(0,0,0,0.06)';
}

function getSubtitleColor(resolvedMode: 'dark' | 'light', highContrast: boolean, fontFamily: ThemePreferences['fontFamily']): string {
  if (fontFamily === 'dyslexia-hc') return '#4A4740';
  if (fontFamily === 'dyslexia') return '#636058';
  if (highContrast) {
    return resolvedMode === 'dark' ? '#B0B8CC' : '#1A1A1A';
  }
  return resolvedMode === 'dark' ? '#8892A8' : '#5A6478';
}

// ---------------------------------------------------------------------------
// Dyslexia typography tokens
// ---------------------------------------------------------------------------

interface DyslexiaTypographyTokens {
  baseFontSize: number;
  smFontSize: number;
  lgFontSize: number;
  xlFontSize: number;
  lineHeight: number;
  lineHeightTight: number;
  letterSpacing: string;
  wordSpacing: string;
  paragraphSpacing: string;
  maxLineWidth: string;
}

const dyslexiaTokens: DyslexiaTypographyTokens = {
  baseFontSize: 17,
  smFontSize: 15,
  lgFontSize: 20,
  xlFontSize: 24,
  lineHeight: 1.8,
  lineHeightTight: 1.5,
  letterSpacing: '0.04em',
  wordSpacing: '0.12em',
  paragraphSpacing: '1.25rem',
  maxLineWidth: '68ch',
};

const dyslexiaHcTokens: DyslexiaTypographyTokens = {
  baseFontSize: 18,
  smFontSize: 16,
  lgFontSize: 22,
  xlFontSize: 26,
  lineHeight: 1.9,
  lineHeightTight: 1.6,
  letterSpacing: '0.05em',
  wordSpacing: '0.14em',
  paragraphSpacing: '1.4rem',
  maxLineWidth: '65ch',
};

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function createCadenceTheme(prefs: Partial<ThemePreferences> = {}): Theme {
  const merged: ThemePreferences = { ...defaultPreferences, ...prefs };

  // Dyslexia themes are always light mode
  const resolvedMode: 'dark' | 'light' = isDyslexiaTheme(merged.fontFamily)
    ? 'light'
    : merged.mode === 'system'
      ? 'dark'
      : merged.mode;

  const palette = getPalette(resolvedMode, merged.highContrast, merged.fontFamily);
  const fontFamily = getFontFamily(merged.fontFamily);
  const baseFontSize = isDyslexiaTheme(merged.fontFamily) ? undefined : fontSizeMap[merged.fontSize];
  const subtitleColor = getSubtitleColor(resolvedMode, merged.highContrast, merged.fontFamily);
  const cardBorder = getCardBorder(resolvedMode, merged.highContrast, merged.fontFamily);
  const dyslexia = isDyslexiaTheme(merged.fontFamily);
  const dt = merged.fontFamily === 'dyslexia-hc' ? dyslexiaHcTokens : dyslexiaTokens;
  // Semantic status colours (green / amber / red) resolved against the mode.
  // Exposed on the theme as `theme.palette.status`.
  const statusColours = getStatusColours(resolvedMode, merged.highContrast);

  // Focus indicator shared across interactive components. Applied globally via
  // `:focus-visible` in the MuiCssBaseline below so every interactive element
  // gets the ring, not just Button / IconButton.
  const focusVisibleStyle = {
    '&.Mui-focusVisible': {
      outline: `${focusRing.width}px solid`,
      outlineColor: 'primary.main',
      outlineOffset: focusRing.offset,
    },
  };

  // macOS-style glass treatment — mirrors the Tailwind Card primitive in
  // src/components/ui/card.tsx so legacy MUI pages (Plan, Calendar, Work
  // Items, Decisions, Strategy, …) read in the same visual language as the
  // new Tailwind dashboard. Skipped for dyslexia themes where high-contrast
  // accessibility outranks aesthetics. Skipped in high-contrast mode for
  // the same reason.
  const useGlass = !dyslexia && !merged.highContrast;
  const glassSurface = useGlass
    ? resolvedMode === 'dark'
      ? {
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          backgroundImage: 'none',
          backdropFilter: 'blur(24px) saturate(150%)',
          WebkitBackdropFilter: 'blur(24px) saturate(150%)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow:
            '0 1px 2px rgba(0, 0, 0, 0.4), 0 22px 60px -20px rgba(0, 0, 0, 0.55)',
          position: 'relative' as const,
          overflow: 'hidden' as const,
          // Top-edge highlight — the way Sonoma / Sequoia panels catch light.
          '&::before': {
            content: '""',
            position: 'absolute',
            insetInline: 0,
            top: 0,
            height: '1px',
            background:
              'linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0) 100%)',
            pointerEvents: 'none' as const,
          },
        }
      : {
          backgroundColor: 'rgba(255, 255, 255, 0.55)',
          backgroundImage: 'none',
          backdropFilter: 'blur(24px) saturate(150%)',
          WebkitBackdropFilter: 'blur(24px) saturate(150%)',
          border: '1px solid rgba(255, 255, 255, 0.45)',
          boxShadow:
            '0 1px 2px rgba(15, 23, 42, 0.04), 0 22px 60px -20px rgba(15, 23, 42, 0.18)',
          position: 'relative' as const,
          overflow: 'hidden' as const,
          '&::before': {
            content: '""',
            position: 'absolute',
            insetInline: 0,
            top: 0,
            height: '1px',
            background:
              'linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(255,255,255,0.7) 50%, rgba(255,255,255,0) 100%)',
            pointerEvents: 'none' as const,
          },
        }
    : {};

  // Build components map
  const components: ThemeOptions['components'] = {
    MuiCard: {
      styleOverrides: {
        root: {
          ...glassSurface,
          backgroundImage: 'none',
          ...(useGlass ? {} : { border: cardBorder, backdropFilter: 'blur(20px)' }),
          ...(dyslexia && { padding: '4px' }), // +15% padding increase
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          fontSize: dyslexia ? `${dt.smFontSize}px` : '0.75rem',
          ...(dyslexia && { letterSpacing: dt.letterSpacing }),
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          fontWeight: 600,
          borderRadius: 8,
          ...(dyslexia && {
            fontFamily,
            letterSpacing: dt.letterSpacing,
            fontSize: `${dt.smFontSize}px`,
          }),
          ...focusVisibleStyle,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          ...focusVisibleStyle,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ ownerState }: { ownerState: { variant?: string; elevation?: number } }) => ({
          backgroundImage: 'none',
          // Apply the same glass treatment to elevated Paper surfaces so
          // dialogs, popovers, menus, search dropdowns etc. all match the
          // Card glass language. Skip flat/outlined Paper because those
          // are usually inline form scaffolding where transparency reads
          // as a bug. AppBar uses its own slot; skip there too.
          ...(useGlass
          && ownerState.variant !== 'outlined'
          && (ownerState.elevation ?? 1) > 0
            ? glassSurface
            : {}),
        }),
      },
    },
    // Form inputs inherit dyslexia typography
    ...(dyslexia && {
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontFamily,
            fontSize: `${dt.baseFontSize}px`,
            letterSpacing: dt.letterSpacing,
            wordSpacing: dt.wordSpacing,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            fontFamily,
            letterSpacing: dt.letterSpacing,
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            fontFamily,
            letterSpacing: dt.letterSpacing,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontFamily,
            fontSize: `${dt.baseFontSize}px`,
            letterSpacing: dt.letterSpacing,
            wordSpacing: dt.wordSpacing,
          },
        },
      },
      // Tooltips
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontFamily,
            fontSize: `${dt.smFontSize}px`,
            letterSpacing: dt.letterSpacing,
            lineHeight: dt.lineHeightTight,
          },
        },
      },
      // Dialog content
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontFamily,
            letterSpacing: dt.letterSpacing,
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            fontFamily,
            letterSpacing: dt.letterSpacing,
            wordSpacing: dt.wordSpacing,
            lineHeight: dt.lineHeight,
          },
        },
      },
      // Table cells — increased padding
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontFamily,
            fontSize: `${dt.smFontSize}px`,
            letterSpacing: dt.letterSpacing,
            padding: '12px 20px', // ~25% increase from default 8px 16px
          },
        },
      },
      // List items — increased spacing
      MuiListItem: {
        styleOverrides: {
          root: {
            marginBottom: '0.25rem',
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            fontFamily,
            letterSpacing: dt.letterSpacing,
            paddingTop: 10,
            paddingBottom: 10,
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: {
            fontFamily,
            letterSpacing: dt.letterSpacing,
          },
          secondary: {
            fontFamily,
            letterSpacing: dt.letterSpacing,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            fontFamily,
            letterSpacing: dt.letterSpacing,
            fontSize: `${dt.smFontSize}px`,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          message: {
            fontFamily,
            letterSpacing: dt.letterSpacing,
            wordSpacing: dt.wordSpacing,
            lineHeight: dt.lineHeight,
          },
        },
      },
    }),
  };

  // -------------------------------------------------------------------------
  // MuiCssBaseline: a single merged set of global rules. Built once per theme
  // build so there is no per-render cost. Everything that touches *, :root, or
  // media queries lives here to stay auditable.
  // -------------------------------------------------------------------------
  const primaryMain = (palette as { primary?: { main?: string } })?.primary?.main ?? '#6C9BFF';

  const cssBaselineOverrides: Record<string, unknown> = {
    // Global :focus-visible ring. Applies to any natively-focusable element
    // (buttons, links, inputs) that is focused via keyboard, not mouse.
    // MUI's Mui-focusVisible class handles its own components; this covers the
    // rest (links, native buttons, inputs inside custom wrappers, etc.).
    ':focus-visible': {
      outline: `${focusRing.width}px solid ${primaryMain}`,
      outlineOffset: focusRing.offset,
      borderRadius: '2px',
    },
    // When the mouse is the input, skip the ring to avoid noise.
    ':focus:not(:focus-visible)': {
      outline: 'none',
    },

    // OS-level reduced-motion honours the system preference even if the user
    // has not set the app pref. If the user HAS set the app pref, the
    // explicit block below forces the same behaviour regardless of OS flag.
    '@media (prefers-reduced-motion: reduce)': {
      '*, *::before, *::after': {
        animationDuration: '0.01ms !important',
        transitionDuration: '0.01ms !important',
        scrollBehavior: 'auto !important',
      },
    },
    // Note: no global `* { transition-duration }` cap. MUI components set
    // their own durations (often below the §7.4 200ms ceiling) and a global
    // rule would lengthen them. The 200ms cap is policy, enforced in review.
  };

  // Soft gradient mesh backdrop on the body. Same intent as the Tailwind
  // dashboard's gradient blobs — gives the glass surfaces above something
  // saturated to refract. Kept off in dyslexia / high-contrast modes.
  if (useGlass) {
    cssBaselineOverrides.body = {
      background:
        resolvedMode === 'dark'
          ? `linear-gradient(135deg, #0a1020 0%, #0f0f1f 50%, #100716 100%)`
          : `linear-gradient(135deg, #eff5ff 0%, #f4f0ff 50%, #fff0f3 100%)`,
      backgroundAttachment: 'fixed' as const,
      minHeight: '100vh',
    };
    cssBaselineOverrides['body::before'] = {
      content: '""',
      position: 'fixed' as const,
      inset: 0,
      pointerEvents: 'none' as const,
      zIndex: -1,
      // Layered radial blobs that bleed through the translucent panels.
      background:
        resolvedMode === 'dark'
          ? 'radial-gradient(circle at 12% 18%, rgba(96, 130, 255, 0.20) 0px, transparent 380px), radial-gradient(circle at 85% 30%, rgba(178, 132, 255, 0.18) 0px, transparent 360px), radial-gradient(circle at 60% 90%, rgba(255, 138, 101, 0.14) 0px, transparent 320px)'
          : 'radial-gradient(circle at 12% 18%, rgba(108, 155, 255, 0.30) 0px, transparent 380px), radial-gradient(circle at 85% 30%, rgba(178, 132, 255, 0.25) 0px, transparent 360px), radial-gradient(circle at 60% 90%, rgba(255, 195, 143, 0.22) 0px, transparent 320px)',
    };
  }

  if (merged.reducedMotion) {
    cssBaselineOverrides['*, *::before, *::after'] = {
      animationDuration: '0.01ms !important',
      transitionDuration: '0.01ms !important',
      scrollBehavior: 'auto !important',
    };
  }

  if (dyslexia) {
    // Force left-align on body text (not headings/labels).
    cssBaselineOverrides['p, li, td, dd, blockquote, .MuiTypography-body1, .MuiTypography-body2'] = {
      textAlign: 'left',
    };
    cssBaselineOverrides['*'] = {
      ...(cssBaselineOverrides['*'] as Record<string, unknown>),
      textAlignLast: undefined,
    };
  }

  components.MuiCssBaseline = {
    styleOverrides: cssBaselineOverrides,
  };

  // Attach semantic status colours to the palette. All variants are object
  // literals (see getPalette), so a spread is safe; cast is only to satisfy
  // the union type.
  const paletteWithStatus = {
    ...(palette as Record<string, unknown>),
    status: statusColours,
  } as ThemeOptions['palette'];

  const themeOptions: ThemeOptions = {
    palette: paletteWithStatus,
    typography: dyslexia
      ? {
          fontFamily,
          fontSize: dt.baseFontSize,
          h4: {
            fontWeight: 700,
            fontSize: `${dt.xlFontSize}px`,
            lineHeight: dt.lineHeightTight,
            letterSpacing: '-0.01em',
          },
          h5: {
            fontWeight: 600,
            fontSize: `${dt.lgFontSize}px`,
            lineHeight: dt.lineHeightTight,
            letterSpacing: '0em',
          },
          h6: {
            fontWeight: 600,
            fontSize: `${dt.baseFontSize + 1}px`,
            lineHeight: dt.lineHeightTight,
          },
          subtitle1: {
            fontWeight: 500,
            color: subtitleColor,
            fontSize: `${dt.baseFontSize}px`,
            lineHeight: dt.lineHeight,
            letterSpacing: dt.letterSpacing,
          },
          subtitle2: {
            fontWeight: 500,
            fontSize: `${dt.smFontSize}px`,
            lineHeight: dt.lineHeight,
            letterSpacing: dt.letterSpacing,
          },
          body1: {
            fontSize: `${dt.baseFontSize}px`,
            lineHeight: dt.lineHeight,
            letterSpacing: dt.letterSpacing,
            wordSpacing: dt.wordSpacing,
          },
          body2: {
            fontSize: `${dt.smFontSize}px`,
            lineHeight: dt.lineHeight,
            letterSpacing: dt.letterSpacing,
            wordSpacing: dt.wordSpacing,
            color: subtitleColor,
          },
          caption: {
            fontSize: '13px',
            lineHeight: dt.lineHeightTight,
            letterSpacing: dt.letterSpacing,
          },
          button: {
            fontFamily,
            letterSpacing: dt.letterSpacing,
            fontSize: `${dt.smFontSize}px`,
          },
        }
      : {
          fontFamily,
          fontSize: baseFontSize,
          h4: {
            fontWeight: 700,
            letterSpacing: '-0.02em',
          },
          h5: {
            fontWeight: 600,
            letterSpacing: '-0.01em',
          },
          h6: {
            fontWeight: 600,
          },
          subtitle1: {
            fontWeight: 500,
            color: subtitleColor,
          },
          body2: {
            color: subtitleColor,
          },
        },
    shape: {
      borderRadius: 12,
    },
    components,
  };

  return createTheme(themeOptions);
}

// ---------------------------------------------------------------------------
// Default export — backward-compatible dark theme
// ---------------------------------------------------------------------------

const theme = createCadenceTheme(defaultPreferences);
export default theme;
